/**
 * @module company.actions
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { statuses } from '~/config/settings/notification.settings';
import { defaultLang } from '~/config/settings/app.settings';
import {
  FetchCompanyDocument,
  FetchCompanyQueryResult,
  FetchSendingEmailAddressesDocument,
  FetchSendingEmailAddressesQueryResult,
} from '~/gql/graphql';
import * as languages from '../languages';
import * as schemas from '../schemas';
import * as transformers from '../transformers';
import { RootState } from '../store';
import { CompaniesDict, SendingEmailAddress } from '~/interfaces/company.interface';
import { Language } from '~/interfaces/employee.interface';

const fetchCompanyList = createAsyncThunk('company/fetchCompanyList', async () => {
  const url = `${host}/companies`;
  const res = await httpRequest<any>(url);
  const companies = transformers.companiesFrom(res.payload.companies);
  const normalizedData = normalize(companies, [schemas.company]);

  return normalizedData;
});

const fetchCompaniesMetadata = createAsyncThunk('company/fetchCompaniesMetadata', async () => {
  const url = `${host}/companies/metadata`;
  const res = await httpRequest<any>(url);
  // Only id + counts are kept so merging into existing company entities never
  // clobbers fields owned by the company list.
  const companiesMetadata = res.payload.companiesMetadata.map((meta) => ({
    id: Number(meta.id),
    enabledEmployeesCount: meta.enabledEmployeesCount,
    completedSurveysCount: meta.completedSurveysCount,
    inProgressSurveysCount: meta.inProgressSurveysCount,
  }));
  const normalizedData = normalize(companiesMetadata, [schemas.company]);

  return normalizedData;
});

const fetchCompany = createAsyncThunk('company/fetchCompany', async (companyId: number) => {
  const res = await graphqlRequest<FetchCompanyQueryResult>(FetchCompanyDocument, { companyId });
  const company = transformers.companyFrom(res.data?.company);
  const normalizedData = normalize(company, schemas.company);

  return normalizedData;
});

const createCompany = createAsyncThunk<CompaniesDict, { name: string; salesforceId?: string }, { state: RootState }>(
  'company/createCompany',
  async ({ name, salesforceId }, { getState, rejectWithValue }) => {
    const state = getState();
    const url = `${host}/companies`;
    const res = await httpRequest<any>(url, {
      method: 'POST',
      body: {
        name,
        salesforceId,
      },
    });

    if (res.error) return rejectWithValue(res.error);

    const rawCompany = res?.payload?.company;

    // HACK: This is a temporary patch to deal with `languageIds`. Some endpoints
    // return `languages` object, and new ones return `languageIds` and need to
    // combine the two to form `supportedLanguages` consistently.
    if (rawCompany.languageIds) {
      rawCompany.supportedLanguages = rawCompany.languageIds.map((langId) => {
        return state.entities.languages[langId]?.code;
      });
    }

    const company = transformers.companyFrom(rawCompany);
    const normalizedData = normalize(company, schemas.company);

    return normalizedData;
  },
);

const modifyCompany = createAsyncThunk<
  CompaniesDict,
  {
    companyId: number;
    name: string;
    color: string;
    logoUrl: string;
    salesforceId?: string;
    timezoneId?: number;
    languageId?: number;
    supportedLanguages: string[];
    companyFeatureSettings?: { [featureName: string]: boolean };
  },
  { state: RootState }
>(
  'company/modifyCompany',
  async (
    {
      companyId,
      name,
      color,
      logoUrl,
      salesforceId,
      timezoneId,
      languageId,
      supportedLanguages,
      companyFeatureSettings,
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState();
    // @ts-ignore @TODO Epic WT-21600
    const languageEntities: Language[] = Object.values(state.entities.languages);

    const supportedLanguageIds = supportedLanguages.map((lang) => {
      const languageEntity = languageEntities.find((language) => language.code === lang);

      return languageEntity?.id;
    });

    const url = `${host}/companies/${companyId}`;
    const updateDetails = {
      name,
      color,
      logoUrl,
      timezoneId,
      salesforceId,
      languageId,
      supportedLanguageIds,
      companyFeatureSettings,
    };
    const res = await httpRequest<any>(url, {
      method: 'PATCH',
      body: updateDetails,
    });

    if (res.error) return rejectWithValue(res.error);

    const rawCompany = res?.payload?.company;

    // HACK: This is a temporary patch to deal with `languageIds`. Some endpoints
    // return `languages` object, and new ones return `languageIds` and need to
    // combine the two to form `supportedLanguages` consistently.
    if (rawCompany.languageIds) {
      rawCompany.supportedLanguages = rawCompany.languageIds.map((langId) => {
        return state.entities.languages[langId]?.code;
      });
    }

    const company = transformers.companyFrom(rawCompany);
    const normalizedData = normalize(company, schemas.company);

    return {
      ...normalizedData,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.companyUpdated'),
        status: statuses.success,
      },
    };
  },
);

const fetchCompanyKioskSettings = createAsyncThunk<CompaniesDict, { companyId: number; lang?: string }>(
  'company/fetchCompanyKioskSettings',
  async ({ companyId, lang = defaultLang }) => {
    const url = `${host}/companies/${companyId}/kiosk?lang=${lang}`;
    const res = await httpRequest<any>(url);

    // NOTE: kioskConfig is a set of company attributes that can be appended to
    // a company object as extra company-data.
    const company = transformers.companyFromKiosk(res?.payload?.kioskConfig);
    const normalizedData = normalize(company, schemas.company);

    return normalizedData;
  },
);

const updateCompanyKioskSettings = createAsyncThunk<
  CompaniesDict,
  { companyId: number; kioskAttributeId?: number | null; kioskLanguage?: string },
  { state: RootState }
>('company/updateCompanyKioskSettings', async ({ companyId, kioskAttributeId, kioskLanguage }, { getState }) => {
  const state = getState();
  const languageId = state.account?.langId || defaultLang;
  let updateInput: {
    kioskAttributeId?: number | null;
    kioskLanguage?: { welcomeText?: string; languageId: number };
  } = {};
  if (kioskAttributeId !== undefined) updateInput.kioskAttributeId = kioskAttributeId;
  if (kioskLanguage !== undefined) {
    updateInput.kioskLanguage = { welcomeText: kioskLanguage, languageId };
  }

  const url = `${host}/companies/${companyId}`;
  const res = await httpRequest<any>(url, {
    method: 'PATCH',
    body: updateInput,
  });
  // @TODO maybe create a new transform for this request?
  const transformedCompany = transformers.companyFromKiosk(res?.payload?.company);
  const company = {
    id: transformedCompany.id,
    kioskAttributeId: transformedCompany.kioskAttributeId,
    kioskLanguage: transformedCompany.kioskLanguage,
  };
  const normalizedData = normalize(company, schemas.company);

  return normalizedData;
});

const changeLogo = createAsyncThunk<{ logoUrl: string }, { companyId: number; file: File }>(
  'company/changeLogo',
  async ({ companyId, file }) => {
    const formData = new FormData();
    formData.append('logo', file);

    const url = `${host}/companies/${companyId}/logo`;
    const res = await httpRequest<any>(url, {
      method: 'POST',
      body: formData,
      skipContentType: true, // Content-Type is automatically set to multipart-form because of FromData API.
    });
    const logoUrl = res?.payload?.logo;

    return {
      logoUrl,
    };
  },
);

const fetchSendingEmailAddresses = createAsyncThunk<SendingEmailAddress, { companyId: number }>(
  'company/fetchSendingEmailAddresses',
  async ({ companyId }, { rejectWithValue }) => {
    const res = await graphqlRequest<FetchSendingEmailAddressesQueryResult>(FetchSendingEmailAddressesDocument, {
      id: companyId,
    });

    if (res.error) return rejectWithValue(res.error);

    const sendingEmailAddresses = transformers.sendingEmailAddressesFrom(
      res.data?.getCompanyConfigDetails?.sendingDomains,
    );
    const normalizedData = normalize(sendingEmailAddresses, [schemas.sendingEmailAddress]);

    return normalizedData;
  },
);

const deleteCompany = createAsyncThunk<Notification | unknown, { companyId: number }>(
  'company/deleteCompany',
  async ({ companyId }, { rejectWithValue }) => {
    const res = await httpRequest<any>(`${host}/api/company/v1/companies/${companyId}`, {
      method: 'DELETE',
    });

    if (res.error) return rejectWithValue(res.error);

    return {
      companyId,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.message.companyDeleted'),
        status: statuses.success,
      },
    };
  },
);

export const companyActions = {
  fetchCompanyList,
  fetchCompaniesMetadata,
  fetchCompany,
  fetchCompanyKioskSettings,
  updateCompanyKioskSettings,
  createCompany,
  modifyCompany,
  changeLogo,
  fetchSendingEmailAddresses,
  deleteCompany,
};
